import { Trans, useLingui } from '@lingui/react/macro';
import { Checkmark } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { useMutationState } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import { ComponentLocations } from 'src/analytics/trackingTypes';

import type { InvoiceCheckoutResponseBody } from 'src/api/endpoints/storeApi.types';
import { queryKeys } from 'src/api/queries';
import { useStripeSubscriptionSuspense } from 'src/api/queries/subscriptions';
import { Button, CardContent, Feedback, Sheet, Spinner } from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { Link2 } from 'src/components/Link2';
import { StatusTag } from 'src/components/StatusTag';
import { formatDate } from 'src/utils/formatDate';
import { formatAmount } from 'src/utils/productUtils';
import { URLS } from 'src/utils/urls/urls';

export const DmOfferConfirmationPage = () => {
  const { t } = useLingui();
  const subscription = useStripeSubscriptionSuspense();
  const [checkoutData] = useMutationState({
    filters: { mutationKey: queryKeys.checkout.withoutCard },
    select: (mutation) => {
      return mutation.state.data as { data: InvoiceCheckoutResponseBody };
    },
  });

  if (subscription.isPending) {
    return <Spinner />;
  }

  if (!subscription) {
    throw new Error('Subscription not found');
  }

  let orderData = checkoutData;
  if (!orderData) {
    // for testing mode only -- mock hack
    const orderFromStorage = localStorage.getItem('dmOrder');
    if (!orderFromStorage) {
      return <Navigate to="/subscriptions" />;
    }
    orderData = JSON.parse(orderFromStorage) as {
      data: InvoiceCheckoutResponseBody;
    };
  }

  return (
    <AppPage.Content className="max-w-ll pt-24 lm:pt-64">
      <Sheet data-cy="confirmation-card" className="w-full">
        <div>
          <CardContent>
            <div className="flex flex-col gap-12 pb-32">
              <Typography variant="responsive-heading-md">
                <Trans>Welcome to Connect</Trans>
              </Typography>
              <Typography variant="body-md">
                <Trans>
                  You&apos;ve successfully activated your Connect subscription.
                  Now you can take your notes even further.{' '}
                  <Link2
                    to="/store/connect"
                    inline
                    componentLocation={ComponentLocations.CHECKOUT.CONFIRMATION}
                  >
                    Read more
                  </Link2>
                </Trans>
              </Typography>
            </div>
          </CardContent>
          <CardContent borderTop>
            <div className="flex flex-row flex-wrap items-center">
              <Typography
                className="w-full lm:w-fit"
                variant="heading-subtle-2xl"
                data-cy="confirmation-card-heading-title"
              >
                {/* Dm offer should be deprecated, if we start handing them out again adjust to be dynamic */}
                <Trans>30-day free trial</Trans>
              </Typography>
              <StatusTag className="mr-auto lm:ml-12" status="Active" />
              <Typography
                variant="interface-xl"
                fontWeight="semibold"
                leadingNone
                data-cy="confirmation-card-heading-amount"
              >
                {formatAmount(
                  orderData.data.data.orderItem.amount,
                  orderData.data.data.orderItem.currency
                )}
              </Typography>
            </div>
          </CardContent>

          <CardContent borderTop>
            <div className="flex flex-row items-center justify-between">
              <p className="m-0 text-16">
                <Trans>Your trial will end:</Trans>
              </p>
              <p
                data-cy="checkout-summary-billing-start-date"
                className="m-0 text-16"
              >
                {formatDate(
                  subscription.data?.paymentInformation?.nextInvoice?.dueDate
                )}
              </p>
            </div>
            <div className="flex flex-row justify-between">
              <p className="m-0 text-16 whitespace-nowrap">
                <Trans>Order id:</Trans>
              </p>
              <p className="m-0 text-right text-16" data-cy="checkout-order-id">
                {orderData.data.data.orderId}
              </p>
            </div>
          </CardContent>
        </div>
        <CardContent borderTop>
          <div className="flex flex-col items-center justify-between gap-32">
            <p className="m-0 text-16">
              <Trans>
                We&apos;ll send you a reminder email before your free
                subscription period ends. View your current subscription or
                update your payment card details in{' '}
                <Link2
                  to="/subscriptions"
                  inline
                  componentLocation={ComponentLocations.CHECKOUT.CONFIRMATION}
                >
                  Subscription
                </Link2>
                .
              </Trans>
            </p>
            <Button
              type="button"
              as="a"
              className="ml-auto w-full lm:w-fit"
              variant="primary"
              to={URLS.STORE_DM_OFFER_SETUP_COMPLETE}
            >
              <span>
                <Trans>Next</Trans>
              </span>
              <Checkmark />
            </Button>
          </div>
        </CardContent>
      </Sheet>

      <Feedback
        className="my-32"
        feedbackTargetId="store-dm-offer-checkout"
        title={t`Did you find what you were looking for?`}
      />
    </AppPage.Content>
  );
};
